import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import AppComponent from '../components/component';
import { useSettings } from '../context/SettingsContext';
import { useMemberAuth } from '../context/MemberAuthContext';
import { useChatBot } from '../context/ChatBotContext';
import { formatTimeRange } from '../config/dates';
import { apiFetch } from '../lib/supabase';
import UserHeader from '../components/UserHeader';
import MaintenanceBanner from '../components/MaintenanceBanner';
import './home.css';

const Home = () => {
  const { getSeasonConfig, getOpeningTimes, loading, isRegistrationOpen, isSeasonClosed, getSeasonClosedMessage } = useSettings();
  const { isAuthenticated, member } = useMemberAuth();
  const { setPageContent, clearPageContent } = useChatBot();
  const SEASON_CONFIG = getSeasonConfig();
  const OPENING_TIMES = getOpeningTimes();
  const [hasRenewedCurrentYear, setHasRenewedCurrentYear] = useState(false);
  const [memberData, setMemberData] = useState(null);
  const [schedule, setSchedule] = useState([]);

  useEffect(() => {
    fetch('/api/public/opening-times')
      .then(r => r.json())
      .then(data => { if (data.schedule) setSchedule(data.schedule); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isAuthenticated && member?.email) {
      const checkMembershipYear = async () => {
        try {
          const { member: m, hasCurrentYear } = await apiFetch(
            `/api/member/by-email-with-current-year?email=${encodeURIComponent(member.email)}`
          );
          if (m) {
            setMemberData(m);
            setHasRenewedCurrentYear(hasCurrentYear);
            if (hasCurrentYear) return;
          }
          const { member: memberOnly } = await apiFetch(
            `/api/member/by-email?email=${encodeURIComponent(member.email)}`
          );
          if (memberOnly) setMemberData(memberOnly);
          setHasRenewedCurrentYear(false);
        } catch (err) {
          console.error("Error checking membership year:", err);
        }
      };
      checkMembershipYear();
    }
  }, [isAuthenticated, member]);

  const currentYear = new Date().getFullYear();
  const seasonClosed = isSeasonClosed();
  const seasonClosedMessage = getSeasonClosedMessage();
  const regularEveningTime = formatTimeRange(
    OPENING_TIMES.REGULAR.EVENING.START,
    OPENING_TIMES.REGULAR.EVENING.END
  );
  const lateSeasonTime = formatTimeRange(
    OPENING_TIMES.REGULAR.EVENING.START,
    OPENING_TIMES.REGULAR.EVENING.LATE_SEASON_END
  );
  const weekendTime = formatTimeRange(
    OPENING_TIMES.REGULAR.WEEKEND.START,
    OPENING_TIMES.REGULAR.WEEKEND.END
  );
  const holidayTime = formatTimeRange(
    OPENING_TIMES.SCHOOL_HOLIDAYS.START,
    OPENING_TIMES.SCHOOL_HOLIDAYS.END
  );

  useEffect(() => {
    const openingTimesContent = seasonClosed
      ? `Opening Times (${currentYear} Season):
- The pool is CLOSED for the season. There are no sessions running.
- ${seasonClosedMessage}
- Member login is unavailable until the new season opens.`
      : `Opening Times (${currentYear} Season):
- Regular Evenings (Monday, Tuesday, Wednesday, Friday): ${regularEveningTime}
- Late Season Evenings (from late July): ${lateSeasonTime}
- Weekends (Saturday and Sunday): ${weekendTime}
- School Summer Holidays: ${holidayTime}`;

    const content = `CCSC Home Page - Comberton Community Swimming Club

About CCSC:
- Community swimming club based at Comberton Village College pool
- Summer season pool for local families
- Volunteer-run organization

${openingTimesContent}

Membership:
- Individual and Family membership options available
- Members must volunteer for duty shifts (Administrator, 1st Safety Officer, or 2nd Safety Officer)
- Registration opens each year before the summer season

Key Features:
- Outdoor heated pool
- Safe, supervised swimming sessions
- Family-friendly environment
- Duty rota system where all members contribute`;
    
    setPageContent(content);
    return () => clearPageContent();
  }, [setPageContent, clearPageContent, currentYear, regularEveningTime, lateSeasonTime, weekendTime, holidayTime, seasonClosed, seasonClosedMessage]);

  return (
    <div className="home-container10">
      <Helmet>
        <title>Comberton Community Swimming Club</title>
        <meta property="og:title" content="Comberton Community Swimming Club" />
      </Helmet>
      <div className="home-header">
        <span className="home-text10">CCSC</span>
      </div>
      <UserHeader />
      <MaintenanceBanner />
      <div className="home-container11">
        <div className="home-container12">
          <div className="home-container13">
            <span className="home-text11">
              <span>Comberton Community</span>
              <br></br>
              <span>Swimming Club</span>
            </span>
            <span className="home-text15">A pool for and run by members</span>
      {isAuthenticated ? (
              hasRenewedCurrentYear ? (
                <>
                  <Link to="/members" className="home-button">Member Portal</Link>
                  <Link to="/members" className="home-button2 Button2">Member Portal</Link>
                </>
              ) : isRegistrationOpen() ? (
                <>
                  <Link to="/register" state={{ isRenewal: true, memberData }} className="home-button">Renew Membership</Link>
                  <Link to="/register" state={{ isRenewal: true, memberData }} className="home-button2 Button2">Renew Membership</Link>
                </>
              ) : (
                <>
                  <Link to="/members" className="home-button">Member Portal</Link>
                  <Link to="/members" className="home-button2 Button2">Member Portal</Link>
                </>
              )
            ) : isRegistrationOpen() ? (
              <>
                <a href="/register" color="Black" className="home-button">Become a member</a>
                <a href="/register" className="home-button2 Button2">Become a member</a>
              </>
            ) : null} 
          </div>
        </div>
      </div>
      <div className="home-container14">
        <div className="home-container15">
          <span className="home-text16">Season {SEASON_CONFIG.REGISTRATION_YEAR}</span>
          <span className="home-text17">
            <span>{new Date(SEASON_CONFIG.SEASON_START).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })} - {new Date(SEASON_CONFIG.SEASON_END).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}*</span>
            <br></br>
          </span>
        </div>
      </div>
      <div className="home-container16">
        <div className="home-container17"></div>
        <div className="home-container18">
          <span className="home-text20">
            *We may extend the season by a few weeks if the weather allows and
            we have enough members who are prepared to do additional duties.
          </span>
        </div>
      </div>

      {!seasonClosed && schedule.length > 0 && (
        <div className="home-schedule-section">
          <h2 className="home-schedule-title">Pool Sessions — Next 2 Weeks</h2>
          <div className="home-schedule-grid">
            {schedule.map(({ date, sessions, closed }) => {
              const d = new Date(date + 'T12:00:00');
              const weekday = d.toLocaleDateString('en-GB', { weekday: 'long' });
              const dateLabel = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
              return (
                <div key={date} className="home-schedule-day">
                  <div className="home-schedule-weekday">{weekday}</div>
                  <div className="home-schedule-date">{dateLabel}</div>
                  {closed ? (
                    <div className="home-schedule-badge" style={{ backgroundColor: '#fdecea', color: '#c62828', fontWeight: 700 }}>Closed</div>
                  ) : sessions.map((s, i) => (
                    <React.Fragment key={i}>
                      <div className="home-schedule-badge">{s}</div>
                      {d.getDay() === 2 && s.startsWith('18') && (
                        <div className="home-schedule-lane">Lane swimming <strong>only</strong></div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Out of season the opening times are misleading, so the same slot carries
          the closed notice instead. Driven by the admin Settings toggle. */}
      {seasonClosed ? (
        <div className="home-season-closed-section">
          <div className="home-season-closed-card" role="status">
            <img
              className="home-season-closed-icon"
              src="/pocket-watch-200h.png"
              alt=""
            />
            <div className="home-season-closed-body">
              <h2 className="home-season-closed-title">
                The pool is closed for the season
              </h2>
              <p className="home-season-closed-text">{seasonClosedMessage}</p>
            </div>
          </div>
        </div>
      ) : (
      <div className="home-container19">
        <div className="home-container20">
          <AppComponent
            imageSrc="/pocket-watch-200h.png"
            infoTitle2="Opening Times"
          />
          <span className="home-infobody1">
            <span className="home-text21">
              Monday, Tuesday*, Wednesday & Friday{' '}
            </span>
            <span className="home-text22">{regularEveningTime}</span>
            <span className="home-text23">.</span>
            <br className="home-text24"></br>
            <span className="home-text25">Saturday & Sunday </span>
            <span className="home-text26">{weekendTime}</span>
            <span className="home-text27">.</span>
            <br className="home-text28"></br>
            <span className="home-text29">
              *Tuesdays are for lane-swimming only.
            </span>
          </span>
        </div>
        <div className="home-container21">
          <AppComponent
            imageSrc="/owl-200h.png"
            infoTitle2="School Summer Holidays"
          />
          <span className="home-infobody2">
            <span className="home-text30">
              During the school summer holidays we run two extra sessions on{' '}
            </span>
            <br className="home-text31"></br>
            <span className="home-text32">Tuesday & Thursday </span>
            <span className="home-text33">{holidayTime}.</span>
          </span>
        </div>
        <div className="home-container22">
          <AppComponent
            imageSrc="/sunset-200h.png"
            infoTitle2="Daylight Adjustments"
          />
          <span className="home-infobody3">
            <span className="home-text34">
              From {new Date(SEASON_CONFIG.EVENING_TIMES_CHANGE_DATE).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })},
              when it gets darker in the evenings, we change the opening times to{' '}
            </span>
            <span className="home-text35">{lateSeasonTime}.</span>
          </span>
        </div>
      </div>
      )}

      {/* Rest of the component remains the same */}

     <div className="home-container19">
        <div className="home-container20">
          <AppComponent
            imageSrc="/who_can_become_a_member.svg"
            infoTitle2="Who can become a member?"
          />
          <span className="home-infobody1">
            <span className="home-text21">
              Anybody who lives close enough to the pool to make good use of it,
              as well as contribute to the running of the club by helping as a
              safety officer or an administrator.
            </span>
            
          </span>
        </div>
        <div className="home-container21">
          <AppComponent
            imageSrc="/li_pound-sterling.svg"
            infoTitle2="Membership Types &amp; Fees"
          />
          <span className="home-infobody2">
            <span className="home-text33">
              Individual Membership
            </span>
             <br className="home-text31"></br>
            <span className="home-text30">
Membership for only one person. Yearly fee: £55.             </span>
            <br className="home-text31"></br>
             <br className="home-text31"></br>
            <span className="home-text33">Household Membership</span>
<br className="home-text31"></br>
<span className="home-text30">
 Membership for up to 6 people of a household who live at the
                same address. Yearly fee: £70</span>           <br className="home-text31"></br>
              <br className="home-text31"></br>
          </span>
        </div>
        <div className="home-container22">
          <AppComponent
            imageSrc="li_settings.svg"
            infoTitle2="Member Duties"
          />
          <span className="home-infobody3">
            <span className="home-text34">
              Members are expected to &apos;do their bit&apos; to keep the club
              running for everyone. There are 2 types of duties that members can
              choose to take on.
            </span>

            <br className="home-text31"></br>
             <br className="home-text31"></br>
                         <span className="home-text33">Administrator</span>
            <br className="home-text31"></br>
            <span className="home-text32">Administrators help with the running of the club by manning
                    our admin hut to administer pool entrants and do general
                    support tasks during opening hours. No formal training is
                    needed but you will require up-to-date knowledge of the club
                    rules (which will be provided).</span>
                              <br className="home-text31"></br>
             <br className="home-text31"></br>
            <span className="home-text33">Safety Officer</span>
            <br className="home-text31"></br>
            <span className="home-text32">To safely run the pool we need 2 safety officers on the pool
                    side. Members who choose to contribute as a safety officer
                    will receive the appropriate training to act as a first or
                    second safety officer. First safety officers will also
                    receive RLSS life support training and will need to
                    successfully complete an exam every 2 years.</span>
                              <br className="home-text31"></br>
             <br className="home-text31"></br>
            <span className="home-text31">Safety officers receive training before the start of the season.</span>


          </span>                      

                   
                      
        </div>
      </div>
     

      <div className="home-container32">
        <span className="home-text74 ParagraphRegular">
          © Comberton Community Swimming Club, {currentYear}.
        </span>
      </div>
    </div>
  );
};

export default Home;